/**
 * 🎛️ **The game menu — Escape, and the front door** (T187).
 *
 * 🎯 **haugur, 2026-09-11:** *"i want a menu, when a player presses escape in
 * the game i want a menu popping up, same menu should be used when you press
 * play on the website. menu should be big and prominent and have a few tabs."*
 *
 * ## What it is
 *
 * Four tabs — **Main**, **Audio**, **Graphics**, **World map** — on a panel
 * centred over a dimmed world, with the tabs across the top.
 *
 * 🔴 **It shipped FULL BLEED and haugur changed it the same evening**, after
 * playing it: 🎯 *"i have changed my mind, i want menu A, b ist to large."*
 * ⚠️ **He had chosen the full-bleed one that morning off a still picture**
 * (`Design/samples/31_menu_b_fullbleed.png`) **and rejected it once he was
 * standing in it.** 👉 **The lesson is worth more than the layout: a still
 * frame can settle whether a thing is LEGIBLE and cannot settle whether it is
 * too BIG.** Scale in use is judged against your own screen, mid-game, with
 * something you were about to do.
 *
 * ⚠️ **The World map tab (T189) is the one that will push back on this**, since
 * a map wants room. The panel is `min(940px, 100vw - 48px)` and the panes
 * scroll inside it; if a map needs more, **that tab can widen the panel rather
 * than the menu going back to full screen.**
 *
 * 🎨 **It is the first part of the GAME to use the website's visual language.**
 * `Design/00_Visual_Language.md` §9 has said since 2026-08-10 that the game's
 * interface is *"owed, not blocked"*. The HUD is deliberately outside it — a
 * translucent instrument panel over the world — but this is not a HUD. It is a
 * door with the world behind it, which is exactly what Kvöldvaka is for.
 *
 * ## 🔴 It cannot pause, and it says so
 *
 * **The world is shared.** Other people are in it, the clock runs, and the
 * weather keeps taking your food (`exposure.ts`) whether or not this is open.
 * ⚠️ **A full-screen menu looks exactly like a pause screen**, and every
 * single-player game has trained people that it is one — so the menu carries a
 * line saying it is not. **Do not remove that line to tidy the layout.** The
 * fault it prevents is somebody opening this, walking away, and starving.
 *
 * ## ⚠️ What this does NOT own
 *
 * The light settings live in `settings.ts` and so does the field of view. This
 * file draws controls and calls the store; it holds no value anybody else can
 * change. See that file's header for why — the short version is that the sun
 * sliders stayed in Dev Tools, so two windows now change one object, and *a
 * second way in that forgets what the first one does* is the fault this repo
 * has recorded five times.
 */

import { parseCoordinate } from '@lofi/shared';
import { insideWorld } from './devtools.ts';
import {
  FOV_MAX,
  FOV_MIN,
  WATER_OPACITY_MAX,
  WATER_OPACITY_MIN,
  graphicsStore,
  lightStore,
  menuStore,
  viewRadiusStore,
  type GraphicsSettings,
  type LightSettings,
  type MenuSettings,
} from './settings.ts';
import { MAX_VIEW_RADIUS, MIN_VIEW_RADIUS } from './streaming.ts';
import { WorldMap, type WorldMapHandlers } from './worldmap.ts';
import { CHUNK_SIZE } from '@lofi/shared';

/** The tabs, in the order they appear. */
export const MENU_TABS = ['Main', 'Audio', 'Graphics', 'World map'] as const;
export type MenuTab = (typeof MENU_TABS)[number];

export interface GameMenuHandlers {
  /**
   * Go to an ISN93 position.
   *
   * @param x easting, @param y **northing** — not world z. ⚠️ The conversion
   *   is the caller's, and is done in exactly one place. Same contract as
   *   `DevToolsHandlers.onTeleport`, deliberately: it is the same function.
   */
  onTeleport(x: number, y: number, label: string): void;
  /** Called when the menu opens or closes, so the game can free the mouse. */
  onOpenChange(open: boolean): void;
  /** What the World map tab needs — see `worldmap.ts`. */
  readonly map: WorldMapHandlers;
}

/**
 * 🎬 The scene buttons, moved here out of Dev Tools.
 *
 * 🎯 **haugur:** *"move the perfect day, winter, norurljós, rain and snow (to
 * be built) to the main menu."*
 *
 * ⚠️ **`preset` and `forceRain` are different fields** on `LightSettings` — the
 * first three are mutually exclusive lenses, rain is an independent switch —
 * so they are described here rather than assumed to be one list.
 *
 * 🔴 **Snow has `apply: null`, and is drawn but not clickable.** It does not
 * exist: there is no snow in `rain.ts` and no snow in the weather pipeline. It
 * is shown as *coming* rather than left out, because haugur named it. ⚠️ **The
 * day snow is built, delete this entry's `null` — a row that says "not built
 * yet" about something that works is the lie pointing the other way, and
 * nothing rereads a label once the thing it denies comes true.**
 */
interface Look {
  readonly name: string;
  readonly gloss: string;
  /** How to turn it on and off, or `null` when there is nothing behind it. */
  readonly apply: ((light: LightSettings, on: boolean) => LightSettings) | null;
  readonly isOn: (light: LightSettings) => boolean;
}

const LOOKS: readonly Look[] = [
  {
    name: 'Perfect day',
    gloss: 'blue sky, sun up',
    apply: (l, on) => ({ ...l, preset: on ? 'day' : 'none' }),
    isOn: (l) => l.preset === 'day',
  },
  {
    name: 'Winter night',
    gloss: 'stars, clear sky, moon up',
    apply: (l, on) => ({ ...l, preset: on ? 'night' : 'none' }),
    isOn: (l) => l.preset === 'night',
  },
  {
    name: 'Norðurljós',
    gloss: 'the best case — Kp 7, clear, no moon',
    apply: (l, on) => ({ ...l, preset: on ? 'aurora' : 'none' }),
    isOn: (l) => l.preset === 'aurora',
  },
  {
    name: 'Rain',
    gloss: 'and being rained on costs you food',
    apply: (l, on) => ({ ...l, forceRain: on }),
    isOn: (l) => l.forceRain,
  },
  {
    name: 'Snow',
    gloss: 'not built yet',
    apply: null,
    isOn: () => false,
  },
];

export class GameMenu {
  readonly root: HTMLDivElement;

  private readonly handlers: GameMenuHandlers;
  private readonly tabButtons = new Map<MenuTab, HTMLButtonElement>();
  private readonly panels = new Map<MenuTab, HTMLElement>();
  private readonly lookButtons: { look: Look; button: HTMLButtonElement }[] = [];

  private readonly coordInput: HTMLInputElement;
  private readonly coordNote: HTMLDivElement;
  private readonly fovValue: HTMLSpanElement;
  private readonly fovInput: HTMLInputElement;
  private readonly volumeValue: HTMLSpanElement;
  private readonly volumeInput: HTMLInputElement;
  private readonly musicSwitch: HTMLButtonElement;
  private readonly effectsSwitch: HTMLButtonElement;
  private readonly distanceInput: HTMLInputElement;
  private readonly distanceValue: HTMLSpanElement;
  private readonly distanceNote: HTMLDivElement;
  private readonly scaleInput: HTMLInputElement;
  private readonly scaleValue: HTMLSpanElement;
  private readonly crispSwitch: HTMLButtonElement;
  private readonly texturesSwitch: HTMLButtonElement;
  private readonly aoSwitch: HTMLButtonElement;
  private readonly waterSwitch: HTMLButtonElement;
  /** 🌊 How clear the water is — T158, D115. */
  private readonly clarityInput: HTMLInputElement;
  private readonly clarityValue: HTMLSpanElement;
  private readonly jitterSwitch: HTMLButtonElement;
  private readonly fadeSwitch: HTMLButtonElement;
  private readonly mipSelect: HTMLSelectElement;

  private readonly map: WorldMap;

  private tab: MenuTab = 'Main';
  private open = false;

  constructor(handlers: GameMenuHandlers) {
    this.handlers = handlers;

    this.root = document.createElement('div');
    this.root.className = 'gamemenu';
    this.root.style.display = 'none';
    /* Announced, not merely visible: this takes the whole screen and the
       keyboard with it, and a screen reader should say so. */
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', 'Game menu');

    /* ⚠️ The scrim is a SEPARATE element from the content, so the content can
       sit on it without inheriting its transparency. V36: an opacity is not a
       value, and text drawn at 70 % over the world is not ink, it is a tint of
       whatever happens to be behind it. */
    const scrim = document.createElement('div');
    scrim.className = 'gamemenu-scrim';
    this.root.appendChild(scrim);

    const body = document.createElement('div');
    body.className = 'gamemenu-body';
    this.root.appendChild(body);

    /* ── the title row ──────────────────────────────────────────────────────
       🎯 **haugur, 2026-09-11, having played the full-bleed one:** *"i have
       changed my mind, i want menu A, b ist to large."* **So the menu is a
       panel on a dimmed world**, the way the website builds a raised surface,
       rather than the whole screen.

       ⚠️ **The wordmark is the heading and the tab name is NOT repeated.** The
       first sample had "Main" as a title *and* as the selected tab — the same
       word twice, six millimetres apart, saying nothing the second time. */
    const title = document.createElement('div');
    title.className = 'gamemenu-title';

    const wordmark = document.createElement('div');
    wordmark.className = 'gamemenu-wordmark';
    wordmark.textContent = 'Land of Fire and Ice';

    /* 🔴 A panel has an edge, so it needs a way out that is not a key. The
       full-bleed one did not: there was nothing to click past. */
    const close = document.createElement('button');
    close.className = 'gamemenu-close';
    close.type = 'button';
    close.textContent = '×';
    close.setAttribute('aria-label', 'Close the menu');
    close.addEventListener('click', () => this.setOpen(false));

    title.append(wordmark, close);
    body.appendChild(title);

    // ── the tabs, across the top ────────────────────────────────────────────
    const rail = document.createElement('div');
    rail.className = 'gamemenu-rail';
    rail.setAttribute('role', 'tablist');
    for (const name of MENU_TABS) {
      const button = document.createElement('button');
      button.className = 'gamemenu-tab';
      button.type = 'button';
      button.textContent = name;
      button.setAttribute('role', 'tab');
      button.addEventListener('click', () => this.setTab(name));
      rail.appendChild(button);
      this.tabButtons.set(name, button);
    }
    body.appendChild(rail);

    // ── the panels ──────────────────────────────────────────────────────────
    const panes = document.createElement('div');
    panes.className = 'gamemenu-panes';
    body.appendChild(panes);

    const main = this.buildMain();
    const audio = this.buildAudio();
    const graphics = this.buildGraphics();

    /* 🗺️ The map is its own module — it is the biggest thing in this menu by
       far, and it fetches its own geometry the first time it is shown. */
    this.map = new WorldMap(handlers.map);
    const mapPane = this.pane('World map');
    mapPane.classList.add('worldmap-pane');
    mapPane.appendChild(this.map.root);

    panes.append(main.root, audio.root, graphics.root, mapPane);

    this.coordInput = main.coordInput;
    this.coordNote = main.coordNote;
    this.fovInput = main.fovInput;
    this.fovValue = main.fovValue;
    this.volumeInput = audio.volumeInput;
    this.volumeValue = audio.volumeValue;
    this.musicSwitch = audio.musicSwitch;
    this.effectsSwitch = audio.effectsSwitch;
    this.distanceInput = graphics.distanceInput;
    this.distanceValue = graphics.distanceValue;
    this.distanceNote = graphics.distanceNote;
    this.scaleInput = graphics.scaleInput;
    this.scaleValue = graphics.scaleValue;
    this.crispSwitch = graphics.crispSwitch;
    this.texturesSwitch = graphics.texturesSwitch;
    this.aoSwitch = graphics.aoSwitch;
    this.waterSwitch = graphics.waterSwitch;
    this.clarityInput = graphics.clarityInput;
    this.clarityValue = graphics.clarityValue;
    this.jitterSwitch = graphics.jitterSwitch;
    this.fadeSwitch = graphics.fadeSwitch;
    this.mipSelect = graphics.mipSelect;

    // ── the play button, across the foot ────────────────────────────────────
    const play = document.createElement('button');
    play.className = 'gamemenu-play';
    play.type = 'button';
    play.addEventListener('click', () => this.setOpen(false));

    const playWord = document.createElement('span');
    playWord.className = 'gamemenu-playword';
    playWord.textContent = 'Play game';
    const playSub = document.createElement('span');
    playSub.className = 'gamemenu-playsub';
    playSub.textContent = 'Esc brings this back · the world is already running';
    play.append(playWord, playSub);
    body.appendChild(play);

    /* 🔴 The "this is not a pause" line. See the header — it is load-bearing,
       not decoration. ⚠️ **Under the Play button rather than beside the tabs**,
       so it is on screen for every tab and reads as the last thing before you
       go back into the world. */
    const notPaused = document.createElement('div');
    notPaused.className = 'gamemenu-notpaused';
    notPaused.textContent =
      '⚠️ The world keeps going while this is open — other people are still in it, and the weather is still on you.';
    body.appendChild(notPaused);

    this.setTab('Main');

    /* 🔴 Subscribed rather than read once. A preset switched on from Dev Tools
       must light the button here, and the store calls back immediately on
       subscribe so these controls are labelled on the frame they are built —
       see `settings.ts` for the blank-bar bug that rule comes from. */
    lightStore.subscribe((light) => this.renderLight(light));
    menuStore.subscribe((menu) => this.renderMenu(menu));
    graphicsStore.subscribe((graphics) => this.renderGraphics(graphics));
    viewRadiusStore.subscribe((radius) => this.renderViewRadius(radius));
  }

  get isOpen(): boolean {
    return this.open;
  }

  setOpen(next: boolean): void {
    if (next === this.open) return;
    this.open = next;
    this.root.style.display = next ? 'block' : 'none';
    this.handlers.onOpenChange(next);
  }

  // ── Main ──────────────────────────────────────────────────────────────────

  private buildMain(): {
    root: HTMLElement;
    coordInput: HTMLInputElement;
    coordNote: HTMLDivElement;
    fovInput: HTMLInputElement;
    fovValue: HTMLSpanElement;
  } {
    const root = this.pane('Main');

    const left = document.createElement('div');
    const right = document.createElement('div');
    root.append(left, right);

    // field of view
    const fov = control('Field of view');
    const fovValue = document.createElement('span');
    fovValue.className = 'gamemenu-value';
    fov.head.appendChild(fovValue);

    const fovInput = document.createElement('input');
    fovInput.type = 'range';
    fovInput.className = 'gamemenu-range';
    fovInput.min = String(FOV_MIN);
    fovInput.max = String(FOV_MAX);
    fovInput.step = '1';
    fovInput.setAttribute('aria-label', 'Field of view, degrees');
    fovInput.addEventListener('input', () => {
      menuStore.set({ ...menuStore.get(), fov: Number(fovInput.value) });
    });
    fov.body.appendChild(fovInput);
    fov.body.appendChild(ends(`${FOV_MIN}° near`, `${FOV_MAX}° wide`));
    left.appendChild(fov.root);

    // go to a coordinate — moved here out of Dev Tools
    const coord = control('Go to a coordinate');
    const row = document.createElement('div');
    row.className = 'gamemenu-row';

    const coordInput = document.createElement('input');
    coordInput.type = 'text';
    coordInput.className = 'gamemenu-input';
    coordInput.placeholder = '355825 399796  ·  or  64.0709, -21.9549';
    coordInput.setAttribute('aria-label', 'ISN93 or latitude/longitude');
    coordInput.addEventListener('input', () => this.previewCoordinate());
    coordInput.addEventListener('keydown', (e) => {
      /* Enter is the button. Stopped from propagating so nothing outside
         treats it as a game key — the same guard the Dev Tools box had. */
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        this.teleportToTypedCoordinate();
      }
    });

    const go = document.createElement('button');
    go.className = 'gamemenu-go';
    go.type = 'button';
    go.textContent = 'Go';
    go.addEventListener('click', () => this.teleportToTypedCoordinate());

    row.append(coordInput, go);
    coord.body.appendChild(row);

    const coordNote = document.createElement('div');
    coordNote.className = 'gamemenu-note';
    coord.body.appendChild(coordNote);
    left.appendChild(coord.root);

    // look at — moved here out of Dev Tools
    const looks = control('Look at');
    const list = document.createElement('div');
    list.className = 'gamemenu-looks';
    for (const look of LOOKS) {
      const button = document.createElement('button');
      button.className = 'gamemenu-look';
      button.type = 'button';

      const name = document.createElement('span');
      name.className = 'gamemenu-lookname';
      name.textContent = look.name;
      const gloss = document.createElement('span');
      gloss.className = 'gamemenu-lookgloss';
      gloss.textContent = look.gloss;
      button.append(name, gloss);

      if (look.apply === null) {
        /* ⚠️ Disabled AND it says why in words, never dimmed alone (V7). */
        button.disabled = true;
        button.classList.add('soon');
      } else {
        const apply = look.apply;
        button.addEventListener('click', () => {
          const light = lightStore.get();
          lightStore.set(apply(light, !look.isOn(light)));
        });
      }

      list.appendChild(button);
      this.lookButtons.push({ look, button });
    }
    looks.body.appendChild(list);
    right.appendChild(looks.root);

    return { root, coordInput, coordNote, fovInput, fovValue };
  }

  // ── Audio ─────────────────────────────────────────────────────────────────

  /**
   * 🔊 **Nothing in this game makes a sound yet.**
   *
   * 🎯 haugur chose to build the tab now, with the values saved and waiting.
   * 🔴 **So the tab says so on its own face, in the first thing you read.**
   * A volume slider that silently controls silence reads as working and is
   * never re-checked — the exact shape of fault this project keeps paying for.
   *
   * ⚠️ **Delete the notice in the same session the first sound plays.**
   */
  private buildAudio(): {
    root: HTMLElement;
    volumeInput: HTMLInputElement;
    volumeValue: HTMLSpanElement;
    musicSwitch: HTMLButtonElement;
    effectsSwitch: HTMLButtonElement;
  } {
    const root = this.pane('Audio');

    const left = document.createElement('div');
    const right = document.createElement('div');
    root.append(left, right);

    const notice = document.createElement('div');
    notice.className = 'gamemenu-notice';
    notice.textContent =
      'There are no sounds in the game yet. These are remembered, and they will be used the day there is something to hear.';
    left.appendChild(notice);

    const volume = control('Overall volume');
    const volumeValue = document.createElement('span');
    volumeValue.className = 'gamemenu-value';
    volume.head.appendChild(volumeValue);

    const volumeInput = document.createElement('input');
    volumeInput.type = 'range';
    volumeInput.className = 'gamemenu-range';
    volumeInput.min = '0';
    volumeInput.max = '100';
    volumeInput.step = '1';
    volumeInput.setAttribute('aria-label', 'Overall volume');
    volumeInput.addEventListener('input', () => {
      menuStore.set({ ...menuStore.get(), masterVolume: Number(volumeInput.value) / 100 });
    });
    volume.body.appendChild(volumeInput);
    volume.body.appendChild(ends('silent', 'loud'));
    left.appendChild(volume.root);

    const switches = control('What you would hear');
    const musicSwitch = document.createElement('button');
    musicSwitch.className = 'gamemenu-switch';
    musicSwitch.type = 'button';
    musicSwitch.addEventListener('click', () => {
      menuStore.set({ ...menuStore.get(), musicOn: !menuStore.get().musicOn });
    });

    const effectsSwitch = document.createElement('button');
    effectsSwitch.className = 'gamemenu-switch';
    effectsSwitch.type = 'button';
    effectsSwitch.addEventListener('click', () => {
      menuStore.set({ ...menuStore.get(), effectsOn: !menuStore.get().effectsOn });
    });

    switches.body.append(musicSwitch, effectsSwitch);
    right.appendChild(switches.root);

    return { root, volumeInput, volumeValue, musicSwitch, effectsSwitch };
  }

  // ── Graphics ──────────────────────────────────────────────────────────────

  /**
   * 🖼️ **The picture settings, moved here out of Dev Tools** (T188).
   *
   * 🎯 **haugur, 2026-09-11:** *"3 Graphics will have all the graphichs stuff
   * from Dev tool etc."*
   *
   * 🔴 **Two groups, and the split is the honest part.** The first three are
   * settings a player changes because of how the game looks or runs on their
   * machine. **The four below are a DIAGNOSTIC** — T94's isolation switches,
   * built to answer one question (*what is making the distant ground crawl?*)
   * by turning things off one at a time. They are here because he asked for all
   * of it, and they **say what they are**, so nobody mistakes a measuring
   * instrument for a preference.
   *
   * 👀 **Render distance was never a control at all** — it was `+` and `-`, and
   * undiscoverable unless somebody told you. ⚠️ **It is the first thing to try
   * when the game feels heavy**, which is exactly why a tester complaining of
   * lag could not have found it.
   */
  private buildGraphics(): {
    root: HTMLElement;
    distanceInput: HTMLInputElement;
    distanceValue: HTMLSpanElement;
    distanceNote: HTMLDivElement;
    scaleInput: HTMLInputElement;
    scaleValue: HTMLSpanElement;
    crispSwitch: HTMLButtonElement;
    texturesSwitch: HTMLButtonElement;
    aoSwitch: HTMLButtonElement;
    waterSwitch: HTMLButtonElement;
    clarityInput: HTMLInputElement;
    clarityValue: HTMLSpanElement;
    jitterSwitch: HTMLButtonElement;
    fadeSwitch: HTMLButtonElement;
    mipSelect: HTMLSelectElement;
  } {
    const root = this.pane('Graphics');
    const left = document.createElement('div');
    const right = document.createElement('div');
    root.append(left, right);

    // ── render distance ─────────────────────────────────────────────────────
    const distance = control('How far you can see');
    const distanceValue = document.createElement('span');
    distanceValue.className = 'gamemenu-value';
    distance.head.appendChild(distanceValue);

    const distanceInput = document.createElement('input');
    distanceInput.type = 'range';
    distanceInput.className = 'gamemenu-range';
    distanceInput.min = String(MIN_VIEW_RADIUS);
    distanceInput.max = String(MAX_VIEW_RADIUS);
    distanceInput.step = '1';
    distanceInput.setAttribute('aria-label', 'How far you can see, in chunks');
    /* ⚠️ `change`, not `input`. Every step rebuilds the fog, re-scans every
       loaded chunk and issues a fresh want-list — doing that on each pixel of a
       drag would stutter badly, and this is the setting most likely to be
       dragged on a slow machine. */
    distanceInput.addEventListener('change', () => {
      viewRadiusStore.set(Number(distanceInput.value));
    });
    distance.body.appendChild(distanceInput);
    distance.body.appendChild(ends('near', 'far'));

    const distanceNote = document.createElement('div');
    distanceNote.className = 'gamemenu-note';
    distance.body.appendChild(distanceNote);
    left.appendChild(distance.root);

    // ── render scale ────────────────────────────────────────────────────────
    const scale = control('Render scale');
    const scaleValue = document.createElement('span');
    scaleValue.className = 'gamemenu-value';
    scale.head.appendChild(scaleValue);

    const scaleInput = document.createElement('input');
    scaleInput.type = 'range';
    scaleInput.className = 'gamemenu-range';
    scaleInput.min = '0.5';
    scaleInput.max = '2';
    scaleInput.step = '0.25';
    scaleInput.setAttribute('aria-label', 'Render scale');
    /* `change`, not `input`: every step resizes the drawing buffer. */
    scaleInput.addEventListener('change', () => {
      graphicsStore.set({ ...graphicsStore.get(), renderScale: Number(scaleInput.value) });
    });
    scale.body.appendChild(scaleInput);
    scale.body.appendChild(ends('faster', 'sharper'));

    const scaleNote = document.createElement('div');
    scaleNote.className = 'gamemenu-note';
    scaleNote.textContent =
      'Above 1 draws bigger and shrinks it down — what a Retina screen does for free.';
    scale.body.appendChild(scaleNote);
    left.appendChild(scale.root);

    // ── crisp ───────────────────────────────────────────────────────────────
    const look = control('Block edges');
    const crispSwitch = document.createElement('button');
    crispSwitch.className = 'gamemenu-switch';
    crispSwitch.type = 'button';
    crispSwitch.addEventListener('click', () => {
      graphicsStore.set({ ...graphicsStore.get(), crisp: !graphicsStore.get().crisp });
    });
    look.body.appendChild(crispSwitch);

    /* 🔴 It says what it COSTS, because this one is not a taste question alone.
       three enables anisotropic filtering only when every filter mode is
       linear, so crisp turns it off for the whole scene — which is why distant
       ground crawls. **That is a price, not a bug** (V26). */
    const crispNote = document.createElement('div');
    crispNote.className = 'gamemenu-note';
    crispNote.textContent =
      'Crisp is hard Minecraft pixels, and it is what the game ships. It costs some smoothing far away.';
    look.body.appendChild(crispNote);
    left.appendChild(look.root);

    // ── moving water ────────────────────────────────────────────────────────
    /* 🌊 T207. ⚠️ Deliberately NOT in the diagnostics box below: those four are
       labelled "a test, not a setting" because a diagnostic that looks like a
       preference is one somebody sets wrong and never revisits. This one is the
       other way round — a real preference, which happens also to be the way to
       turn the feature off without a deploy. */
    const water = control('Moving water');
    const waterSwitch = document.createElement('button');
    waterSwitch.className = 'gamemenu-switch';
    waterSwitch.type = 'button';
    waterSwitch.addEventListener('click', () => {
      graphicsStore.set({ ...graphicsStore.get(), movingWater: !graphicsStore.get().movingWater });
    });
    water.body.appendChild(waterSwitch);

    const waterNote = document.createElement('div');
    waterNote.className = 'gamemenu-note';
    waterNote.textContent =
      'The sea, the lakes and the rivers move. Off leaves the water still, exactly as it was before.';
    water.body.appendChild(waterNote);
    left.appendChild(water.root);

    // ── how clear the water is ──────────────────────────────────────────────
    /* 🌊 T158, D115. 🔴 A slider rather than a constant for the reason this
       whole file exists: nobody building this can see the screen it is drawn
       on. haugur asked for see-through water so fish could be seen under it,
       and HOW see-through is a judgement made by standing in the sea. */
    const clarity = control('Water clarity');
    const clarityValue = document.createElement('span');
    clarityValue.className = 'gamemenu-value';
    clarity.head.appendChild(clarityValue);

    const clarityInput = document.createElement('input');
    clarityInput.type = 'range';
    clarityInput.className = 'gamemenu-range';
    clarityInput.min = String(WATER_OPACITY_MIN);
    clarityInput.max = String(WATER_OPACITY_MAX);
    clarityInput.step = '0.01';
    clarityInput.setAttribute('aria-label', 'Water clarity');
    /* `input`, not `change`: this only moves a uniform, so it can follow the
       drag — which is the whole point of being able to find the number by eye. */
    clarityInput.addEventListener('input', () => {
      graphicsStore.set({ ...graphicsStore.get(), waterOpacity: Number(clarityInput.value) });
    });
    clarity.body.appendChild(clarityInput);
    clarity.body.appendChild(ends('see-through', 'solid'));

    const clarityNote = document.createElement('div');
    clarityNote.className = 'gamemenu-note';
    clarityNote.textContent =
      'How much you can see into the sea, the lakes and the rivers. All the way right is the solid water this game used to have.';
    clarity.body.appendChild(clarityNote);
    left.appendChild(clarity.root);
    // ── the diagnostics ─────────────────────────────────────────────────────
    const crawl = control('What is crawling — a test, not a setting');
    const crawlNote = document.createElement('div');
    crawlNote.className = 'gamemenu-notice';
    crawlNote.textContent =
      'These four are drawn on top of each other. Turn them off one at a time, look at the far ground, and say which one stops the crawling. Leave them all ON for ordinary play.';
    crawl.body.appendChild(crawlNote);

    const isolate = (label: string, change: (g: GraphicsSettings) => GraphicsSettings) => {
      const button = document.createElement('button');
      button.className = 'gamemenu-switch';
      button.type = 'button';
      button.dataset['label'] = label;
      button.addEventListener('click', () => graphicsStore.set(change(graphicsStore.get())));
      crawl.body.appendChild(button);
      return button;
    };

    const texturesSwitch = isolate('Block textures', (g) => ({ ...g, textures: !g.textures }));
    const aoSwitch = isolate('Ambient occlusion', (g) => ({
      ...g,
      ambientOcclusion: !g.ambientOcclusion,
    }));
    const jitterSwitch = isolate('Per-block brightness', (g) => ({ ...g, jitter: !g.jitter }));
    const fadeSwitch = isolate('Fade detail with distance', (g) => ({
      ...g,
      detailFade: !g.detailFade,
    }));

    /* ⚠️ The one that settles it. Level 4 of a 16×16 tile is a single flat
       pixel — nothing left in the texture that could shimmer. If the far ground
       still crawls there, the tile is not the cause and every mipmap theory is
       dead. */
    const mipSelect = document.createElement('select');
    mipSelect.className = 'gamemenu-select';
    mipSelect.setAttribute('aria-label', 'Detail level');
    for (const [value, text] of [
      ['auto', 'Detail level: auto — the GPU chooses'],
      ['0', '0 — full 16×16, never blurred'],
      ['1', '1 — 8×8'],
      ['2', '2 — 4×4'],
      ['3', '3 — 2×2'],
      ['4', '4 — 1×1, one flat colour'],
    ] as const) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = text;
      mipSelect.appendChild(option);
    }
    mipSelect.addEventListener('change', () => {
      graphicsStore.set({
        ...graphicsStore.get(),
        mipLevel: mipSelect.value === 'auto' ? null : Number(mipSelect.value),
      });
    });
    crawl.body.appendChild(mipSelect);
    right.appendChild(crawl.root);

    return {
      root,
      distanceInput,
      distanceValue,
      distanceNote,
      scaleInput,
      scaleValue,
      crispSwitch,
      waterSwitch,
      clarityInput,
      clarityValue,
      texturesSwitch,
      aoSwitch,
      jitterSwitch,
      fadeSwitch,
      mipSelect,
    };
  }

  /**
   * A tab that is named but not built.
   *
   * ⚠️ **It says what it WILL be rather than only that it is coming**, so the
   * name is not the only thing on the page.
   */
  private buildComingSoon(tab: MenuTab): HTMLElement {
    const root = this.pane(tab);
    const notice = document.createElement('div');
    notice.className = 'gamemenu-notice';
    notice.textContent =
      'A map of Iceland you can move around, with the landmarks and towns named on it. Not built yet.';
    root.appendChild(notice);
    return root;
  }

  // ── plumbing ──────────────────────────────────────────────────────────────

  private pane(tab: MenuTab): HTMLElement {
    const root = document.createElement('div');
    root.className = 'gamemenu-pane';
    root.setAttribute('role', 'tabpanel');
    root.setAttribute('aria-label', tab);
    this.panels.set(tab, root);
    return root;
  }

  /**
   * 🐑⚠️ **Open the World map on a starving flock** — T286, the button on the
   * login warning. 🎯 haugur chose it: *"a warning at login with a button to
   * the World map."*
   */
  showOnMap(x: number, worldZ: number, label: string): void {
    this.setOpen(true);
    this.setTab('World map');
    void this.map.focusOn(x, worldZ, label);
  }

  private setTab(tab: MenuTab): void {
    this.tab = tab;
    for (const [name, button] of this.tabButtons) {
      const on = name === tab;
      button.classList.toggle('sel', on);
      button.setAttribute('aria-selected', String(on));
    }
    /* ⚠️ A CLASS, not an inline `display`. The map pane is `display: block`
       (it wants the whole width, not two columns) and an inline `grid` set here
       would beat that class every time — which on screen is a map squashed into
       half a panel with nothing to say why. */
    for (const [name, panel] of this.panels) {
      panel.classList.toggle('sel', name === tab);
    }

    /* 🗺️ 🔴 The map fetches its geometry HERE, the first time it is shown —
       never when the menu is built. Building the menu is on the path into the
       world and the map must not add a byte to it. */
    if (tab === 'World map') void this.map.show();
  }

  /** Draw the stored light onto the *Look at* buttons. */
  private renderLight(light: LightSettings): void {
    for (const { look, button } of this.lookButtons) {
      const on = look.isOn(light);
      button.classList.toggle('on', on);
      button.setAttribute('aria-pressed', String(on));
    }
  }

  /** Draw the stored field of view and audio values onto their controls. */
  private renderMenu(menu: MenuSettings): void {
    this.fovValue.textContent = `${menu.fov.toFixed(0)}°`;
    /* ⚠️ Only written when it differs, so dragging the slider does not fight
       the drag by rewriting the value under the thumb. */
    if (this.fovInput.value !== String(menu.fov)) this.fovInput.value = String(menu.fov);

    const volume = Math.round(menu.masterVolume * 100);
    this.volumeValue.textContent = `${volume}%`;
    if (this.volumeInput.value !== String(volume)) this.volumeInput.value = String(volume);

    switchLabel(this.musicSwitch, 'Music', menu.musicOn);
    switchLabel(this.effectsSwitch, 'Sound effects', menu.effectsOn);
  }

  /** Draw the stored graphics settings onto their controls. */
  private renderGraphics(graphics: GraphicsSettings): void {
    this.scaleValue.textContent = `${graphics.renderScale.toFixed(2)}×`;
    if (this.scaleInput.value !== String(graphics.renderScale)) {
      this.scaleInput.value = String(graphics.renderScale);
    }

    switchLabel(this.crispSwitch, 'Crisp pixels', graphics.crisp);
    switchLabel(this.texturesSwitch, 'Block textures', graphics.textures);
    switchLabel(this.aoSwitch, 'Ambient occlusion', graphics.ambientOcclusion);
    switchLabel(this.waterSwitch, 'Moving water', graphics.movingWater);
    /* 🌊 Read as how CLEAR it is, because that is what the control is called —
       the stored number is how solid it is, and a label that disagrees with its
       own slider is the fault this project keeps paying for. */
    this.clarityValue.textContent = `${Math.round((1 - graphics.waterOpacity) * 100)}% clear`;
    /* ⚠️ Only written when it differs, so dragging does not fight the store. */
    if (this.clarityInput.value !== String(graphics.waterOpacity)) {
      this.clarityInput.value = String(graphics.waterOpacity);
    }
    switchLabel(this.jitterSwitch, 'Per-block brightness', graphics.jitter);
    switchLabel(this.fadeSwitch, 'Fade detail with distance', graphics.detailFade);

    const mip = graphics.mipLevel === null ? 'auto' : String(graphics.mipLevel);
    if (this.mipSelect.value !== mip) this.mipSelect.value = mip;
  }

  /**
   * Draw the render distance, **in metres as well as chunks**.
   *
   * 🔴 **Chunks are our word, not a player's.** A chunk is 32 m, so radius 8 is
   * about 256 m — a number somebody can picture by looking out of a window.
   * ⚠️ **And it says what it costs**, because this is the one setting where
   * the cost is the reason to touch it: the area in view grows with the square,
   * so every step out is more than the last.
   */
  private renderViewRadius(radius: number): void {
    const metres = radius * CHUNK_SIZE;
    this.distanceValue.textContent = `${metres} m`;
    if (this.distanceInput.value !== String(radius)) this.distanceInput.value = String(radius);

    /* ⚠️ The chunk count is the honest measure of what it costs — a radius of 8
       is ~201 chunks in the flat ring, and 24 is nine times that. */
    const chunks = Math.round(Math.PI * radius * radius);
    this.distanceNote.textContent =
      `${radius} chunks of 32 m — roughly ${chunks} to keep loaded. ` +
      (radius > 12
        ? 'Far. If the game feels heavy, this is the first thing to bring down.'
        : 'Bring this down before you blame anything else for a heavy game.');
  }

  // ── coordinates — moved here out of Dev Tools ─────────────────────────────

  /**
   * Say what was understood, before the button is pressed.
   *
   * ⚠️ The whole risk of a box that accepts two formats is silently reading
   * one as the other. Echoing the interpretation back — including that a
   * lat/lon pair was converted, and that a pair arrived reversed — makes a
   * misread visible instead of putting you somewhere unexplained.
   */
  private previewCoordinate(): void {
    const raw = this.coordInput.value.trim();
    if (raw === '') {
      this.coordNote.textContent = '';
      this.coordNote.className = 'gamemenu-note';
      return;
    }

    const parsed = parseCoordinate(raw);
    if (!parsed) {
      this.coordNote.textContent = 'Not a coordinate I recognise';
      this.coordNote.className = 'gamemenu-note bad';
      return;
    }

    const { x, y } = parsed.isn93;
    const how =
      parsed.source === 'latlon'
        ? `lat/lon → ISN93${parsed.swapped ? ' (read as lon, lat)' : ''}`
        : 'ISN93';
    const inside = insideWorld(x, y);
    const where = inside ? '' : ' · ⚠️ outside the world — no ground there';

    this.coordNote.textContent = `${how}: ${x.toFixed(0)}, ${y.toFixed(0)}${where}`;
    this.coordNote.className = inside ? 'gamemenu-note ok' : 'gamemenu-note warn';
  }

  private teleportToTypedCoordinate(): void {
    const parsed = parseCoordinate(this.coordInput.value);
    if (!parsed) {
      this.coordNote.textContent = 'Not a coordinate I recognise';
      this.coordNote.className = 'gamemenu-note bad';
      return;
    }
    const { x, y } = parsed.isn93;
    this.handlers.onTeleport(x, y, `${x.toFixed(0)}, ${y.toFixed(0)}`);
    /* 🔴 Go back to the world. Teleporting and being left staring at a menu is
       the one thing nobody means by pressing Go. */
    this.setOpen(false);
  }
}

// ── small builders ──────────────────────────────────────────────────────────

/** A labelled control: an uppercase label, a slot for a value, and a body. */
function control(label: string): {
  root: HTMLElement;
  head: HTMLElement;
  body: HTMLElement;
} {
  const root = document.createElement('div');
  root.className = 'gamemenu-ctl';

  const head = document.createElement('div');
  head.className = 'gamemenu-ctlhead';
  const name = document.createElement('span');
  name.className = 'gamemenu-label';
  name.textContent = label;
  head.appendChild(name);

  const body = document.createElement('div');
  root.append(head, body);
  return { root, head, body };
}

/** The two words under a slider that say which way is which. */
function ends(low: string, high: string): HTMLElement {
  const root = document.createElement('div');
  root.className = 'gamemenu-ends';
  const a = document.createElement('span');
  a.textContent = low;
  const b = document.createElement('span');
  b.textContent = high;
  root.append(a, b);
  return root;
}

/**
 * ⚠️ Labelled here as well as on click, and the reason is a real bug: haugur
 * reported a Dev Tools switch sitting on screen as **a blank bar**, because its
 * text was only ever written by the "something changed" path. See `settings.ts`.
 */
function switchLabel(button: HTMLButtonElement, label: string, on: boolean): void {
  button.textContent = `${label} · ${on ? 'ON' : 'OFF'}`;
  button.classList.toggle('on', on);
  button.setAttribute('aria-pressed', String(on));
}
